import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
} from "firebase/storage";
import { app, db } from "../firebase";

export const PERSONAL_DATA_COLLECTION = "personal_data";
export const MAX_UPLOAD_BYTES = 1024 * 1024 * 1024;
export const MAX_UPLOAD_LABEL = "1 GB";
const BLOCKED_EXT = [".exe", ".bat", ".cmd", ".msi", ".dll", ".sh", ".js", ".html", ".htm"];

const storage = getStorage(app);

function safeFileName(name) {
  return String(name || "file").replace(/[^a-zA-Z0-9._-]/g, "_");
}

export function validatePersonalFile(file) {
  if (!file) throw new Error("No file selected");
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`File exceeds ${MAX_UPLOAD_LABEL} limit`);
  }
  const lower = String(file.name || "").toLowerCase();
  if (BLOCKED_EXT.some((ext) => lower.endsWith(ext))) {
    throw new Error("This file type is not allowed");
  }
}

/**
 * @param {string} userId
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function listPersonalData(userId) {
  if (!userId) return [];
  const qy = query(
    collection(db, PERSONAL_DATA_COLLECTION),
    where("userId", "==", userId),
    orderBy("updatedAt", "desc")
  );
  try {
    const snap = await getDocs(qy);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    // Fallback when composite index is not ready yet
    const code = String(err?.code || "");
    if (!code.includes("failed-precondition") && !String(err?.message || "").includes("index")) {
      throw err;
    }
    const snap = await getDocs(
      query(collection(db, PERSONAL_DATA_COLLECTION), where("userId", "==", userId))
    );
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
  }
}

/**
 * @param {{ userId: string, title: string, notes?: string, file?: File | null, email?: string, onProgress?: (pct: number) => void }} params
 */
export async function createPersonalData({ userId, title, notes = "", file = null, email = "", onProgress }) {
  if (!userId) throw new Error("Not signed in");
  const trimmed = String(title || "").trim();
  if (!trimmed) throw new Error("Title is required");

  let fileMeta = {};
  if (file) {
    validatePersonalFile(file);
  }

  const refDoc = await addDoc(collection(db, PERSONAL_DATA_COLLECTION), {
    userId,
    email: email || "",
    title: trimmed,
    notes: String(notes || ""),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  if (file) {
    try {
      fileMeta = await uploadPersonalFile(userId, refDoc.id, file, onProgress);
    } catch (err) {
      await deleteDoc(refDoc).catch(() => {});
      throw err;
    }
    await updateDoc(refDoc, { ...fileMeta, updatedAt: serverTimestamp() });
  }

  return { id: refDoc.id, title: trimmed, notes, userId, ...fileMeta };
}

/**
 * @param {string} id
 * @param {{ userId: string, title: string, notes?: string, file?: File | null, existing?: Record<string, unknown>, onProgress?: (pct: number) => void }} params
 */
export async function updatePersonalData(id, { userId, title, notes = "", file = null, existing = {}, onProgress }) {
  if (!userId || !id) throw new Error("Missing id or user");
  if (existing.userId && existing.userId !== userId) {
    throw new Error("You can only edit your own personal data");
  }
  const trimmed = String(title || "").trim();
  if (!trimmed) throw new Error("Title is required");

  const patch = {
    title: trimmed,
    notes: String(notes || ""),
    updatedAt: serverTimestamp(),
  };

  if (file) {
    validatePersonalFile(file);
    Object.assign(patch, await uploadPersonalFile(userId, id, file, onProgress));
    if (existing.storagePath) {
      try {
        await deleteObject(storageRef(storage, existing.storagePath));
      } catch {
        // old file may already be gone
      }
    }
  }

  await updateDoc(doc(db, PERSONAL_DATA_COLLECTION, id), patch);
  return { id, ...existing, ...patch };
}

/**
 * @param {string} id
 * @param {Record<string, unknown>} existing
 * @param {string} userId
 */
export async function deletePersonalData(id, existing, userId) {
  if (!userId || !id) throw new Error("Missing id or user");
  if (existing?.userId && existing.userId !== userId) {
    throw new Error("You can only delete your own personal data");
  }
  if (existing?.storagePath) {
    try {
      await deleteObject(storageRef(storage, existing.storagePath));
    } catch {
      // ignore missing storage object
    }
  }
  await deleteDoc(doc(db, PERSONAL_DATA_COLLECTION, id));
}

async function uploadPersonalFile(userId, docId, file, onProgress) {
  const path = `personal/${userId}/${docId}_${Date.now()}_${safeFileName(file.name)}`;
  const sref = storageRef(storage, path);
  const task = uploadBytesResumable(sref, file, { contentType: file.type || "application/octet-stream" });
  await new Promise((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => {
        if (onProgress && snap.totalBytes > 0) {
          onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
        }
      },
      reject,
      resolve
    );
  });
  const downloadUrl = await getDownloadURL(sref);
  return {
    fileName: file.name,
    contentType: file.type || "",
    size: file.size,
    storagePath: path,
    downloadUrl,
  };
}

export async function triggerFileDownload(item) {
  if (!item?.downloadUrl) throw new Error("No file attached");
  const a = document.createElement("a");
  a.href = item.downloadUrl;
  a.download = item.fileName || "personal-file";
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.click();
}
